/* eslint-disable */
import React, { useEffect, useRef } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import RtlLayout from "layouts/rtl";
import AdminLayout from "layouts/admin";
import AuthLayout from "layouts/auth";
import PrivateRoute from "layouts/PrivateRoute";
import io from "socket.io-client";
import { MyProvider } from "Contextapi/MyContext";
import musicURl from "../src/assets/sound/mixkit-urgent-simple-tone-loop-2976.wav";
import { Box, useMediaQuery, useTheme } from "@mui/material";

// ✅ Safari detection
const isSafari = () => {
  return (
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
    !navigator.userAgent.includes("Chrome")
  );
};

// ✅ Unlock audio playback (Safari requires gesture)
const unlockAudioForSafari = () => {
  const audio = new Audio(musicURl);
  audio.muted = true;
  audio
    .play()
    .then(() => {
      audio.pause();
      audio.muted = false;
      console.log("Audio unlocked for Safari.");
    })
    .catch((err) => {
      console.warn("Safari audio unlock failed:", err);
    });
};

// ✅ Notification permission
const requestNotificationPermission = async () => {
  try {
    if (
      typeof window.safari !== "undefined" &&
      typeof window.safari.pushNotification !== "undefined"
    ) {
      console.warn("Safari Push Notifications require APNs.");
    } else if (Notification.permission !== "granted") {
      await Notification.requestPermission();
    }
  } catch (error) {
    console.error("Notification permission error:", error);
  }
};

const App = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [screenWidth, setScreenWidth] = React.useState('1102');
  const audioPlayer = useRef(new Audio());
  const notifiedOrderIds = useRef(new Set());
  useEffect(() => {
    const handleUserGesture = () => {
      if (isSafari()) unlockAudioForSafari();
      requestNotificationPermission();

      document.removeEventListener("click", handleUserGesture);
      document.removeEventListener("touchstart", handleUserGesture);
    };

    document.addEventListener("click", handleUserGesture);
    document.addEventListener("touchstart", handleUserGesture);
  }, []);

  useEffect(() => {
    const playAudio = () => {
      if (audioPlayer.current) {
        audioPlayer.current.pause();
        audioPlayer.current.currentTime = 0;
        audioPlayer.current.src = musicURl;

        if (isSafari() && document.visibilityState !== "visible") {
          console.log("Safari: skipping sound in background tab.");
          return;
        }

        audioPlayer.current.play().catch((error) => {
          console.error("Error playing audio:", error);
        });
      }
    };

    const socket = io("https://api.medstown.com", {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      timeout: 20000,
    });

    socket.on("connect", () => {
      socket.emit("login", { userId: "TeamDb" });
    });

    const handleNewOrder = (data) => {
      const orderId = data?.order?.orderId;
      if (!orderId || notifiedOrderIds.current.has(orderId)) return;

      notifiedOrderIds.current.add(orderId);
      playAudio();

      if (Notification.permission === "granted") {
        const options = {
          body: `New order received:\nID: ${orderId}\nCustomer Name: ${data?.order?.customerName
            }\nPincode: ${data?.order?.customer_address?.postalCode
            }\nOrdered At: ${new Date(data?.order?.createdAt).toLocaleString()}`,
          icon: "",
          dir: "ltr",
        };

        try {
          const notification = new Notification(`OrderId ${orderId}`, options);
          notification.onclick = () =>
            window.open(
              `https://teamdb.medstown.com/admin/order/individual/${orderId}`,
              "_blank"
            );
        } catch (error) {
          console.error("Error creating notification:", error);
        }
      }
    };

    socket.on("new_order_notify", handleNewOrder);

    return () => {
      socket.off("new_order_notify", handleNewOrder);
      socket.disconnect();
    };
  }, []);

  document.documentElement.dir = "ltr";
  return (
    <Box sx={{
      mt: isMobile ? 8 : isTablet ? 8 : 0,
      mb: isMobile ? 8 : isTablet ? 8 : 0,
    }}>
      <MyProvider>
        <Routes>
          <Route path="auth/*" element={<AuthLayout />} />
          <Route
            path="admin/*"
            element={
              <PrivateRoute>
                <AdminLayout />
              </PrivateRoute>
            }
          />
          <Route path="rtl/*" element={<RtlLayout />} />
          <Route
            path="/"
            element={
              localStorage.getItem("token") ? (
                <Navigate to="/admin/dashboard" replace />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="*"
            element={
              localStorage.getItem("token") ? (
                <Navigate to="/admin/dashboard" replace />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
        </Routes>
      </MyProvider>
    </Box>
  );
};

export default App;
